import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

interface DashboardState {
  view: 'health' | 'incidents';
  setView: (v: 'health' | 'incidents') => void;
  refreshKey: number;
  triggerRefresh: () => void;
  lastRefresh: Date | null;
  isOnline: boolean;
  hasCriticalIncidents: boolean;
  setCriticalFlag: (incidents: Array<{ severity: string }>) => void;
}

const DashboardContext = createContext<DashboardState | null>(null);

export function DashboardProvider({ children }: { children: ReactNode }) {
  const [view, setView] = useState<'health' | 'incidents'>('health');
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasCriticalIncidents, setHasCriticalIncidents] = useState(false);

  const setCriticalFlag = useCallback((incidents: Array<{ severity: string }>) => {
    setHasCriticalIncidents(incidents.some((i) => i.severity === 'P0' || i.severity === 'P1'));
  }, []);

  const triggerRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => {
    triggerRefresh(); // initial load
    const id = setInterval(triggerRefresh, 30_000);
    return () => clearInterval(id);
  }, [triggerRefresh]);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  return (
    <DashboardContext.Provider value={{ view, setView, refreshKey, triggerRefresh, lastRefresh, isOnline, hasCriticalIncidents, setCriticalFlag }}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard(): DashboardState {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be inside DashboardProvider');
  return ctx;
}
